import React, { useState } from 'react';
import {
  Search,
  Plus,
  Eye,
  Edit,
  User,
  Mail,
  Phone,
  Shield,
  CheckCircle,
  XCircle,
  Calendar,
  Filter,
  X,
  Save,
  Lock,
  Unlock,
  UserCheck,
  AlertTriangle,
  Clock,
  MapPin,
  Building2,
  Trash2
} from 'lucide-react';
import { User as UserType } from '../../types';
import { format } from 'date-fns';
import { tr } from 'date-fns/locale';
import AddUserModal from '../Modals/AddUserModal';
import EditUserModal from '../Modals/EditUserModal';
import UserDetailModal from '../Modals/UserDetailModal';
import UserSecurityModal from '../Modals/UserSecurityModal';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../contexts/AuthContext';

const Users: React.FC = () => {
  const { user: currentUser } = useAuth();
  const [searchTerm, setSearchTerm] = useState('');
  const [roleFilter, setRoleFilter] = useState<string>('all');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [showAddModal, setShowAddModal] = useState(false);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showSecurityModal, setShowSecurityModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState<UserType | null>(null);
  const [users, setUsers] = useState<UserType[]>([]);
  const [loading, setLoading] = useState(true);
  const [branches, setBranches] = useState<any[]>([]);
  const [units, setUnits] = useState<any[]>([]);
  const [deletingUserId, setDeletingUserId] = useState<string | null>(null);

  // Kullanıcıları Supabase'den yükle
  React.useEffect(() => {
    fetchUsers();
    fetchBranchesAndUnits();
  }, []);

  const fetchBranchesAndUnits = async () => {
    try {
      if (!supabase) return;

      // Fetch branches
      const { data: branchesData, error: branchesError } = await supabase
        .from('branches')
        .select('*')
        .order('name');

      if (!branchesError) {
        setBranches(branchesData || []);
      }

      // Fetch units
      const { data: unitsData, error: unitsError } = await supabase
        .from('units')
        .select('*')
        .order('name');

      if (!unitsError) {
        setUnits(unitsData || []);
      }
    } catch (error) {
      console.error('Şube ve birimler yüklenirken hata:', error);
    }
  };

  const fetchUsers = async () => {
    try {
      setLoading(true);

      if (!supabase) {
        console.log('Supabase bağlantısı henüz kurulmadı');
        setLoading(false);
        return;
      }

      const { data, error } = await supabase
        .from('profiles')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) {
        console.error('Kullanıcılar yüklenirken hata:', error);
        setLoading(false);
        return;
      }

      // Supabase verilerini app formatına dönüştür
      const appUsers: UserType[] = (data || []).filter(profile => !profile.deleted_at).map(profile => ({
        id: profile.id,
        name: profile.name,
        email: profile.email,
        phone: profile.phone,
        role: profile.role,
        avatar: profile.avatar,
        branchId: profile.branch_id,
        unitId: profile.unit_id,
        unitType: profile.unit_type,
        isActive: profile.is_active,
        permissions: profile.permissions,
        responsibleUniversities: profile.responsible_universities,
        createdAt: new Date(profile.created_at),
        lastLogin: profile.last_login ? new Date(profile.last_login) : undefined
      }));

      setUsers(appUsers);
    } catch (error) {
      console.error('Kullanıcılar yüklenirken hata:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSaveUser = async (userData: any) => {
    // Modal'dan gelen yeni kullanıcı verisi zaten Supabase'e kaydedildi
    // Sadece listeyi yenile
    await fetchUsers();
    setShowAddModal(false);
  };

  const handleUserAction = (action: string, userId: string) => {
    const user = users.find(u => u.id === userId);
    if (!user) return;

    setSelectedUser(user);

    switch (action) {
      case 'view':
        setShowDetailModal(true);
        break;
      case 'edit':
        setShowEditModal(true);
        break;
      case 'security':
        setShowSecurityModal(true);
        break;
      default:
        console.log(`Action: ${action} for user: ${userId}`);
    }
  };

  const handleToggleUserStatus = (userId: string) => {
    setUsers(prev => prev.map(user =>
      user.id === userId ? { ...user, isActive: !user.isActive } : user
    ));
  };

  const handleResetPassword = async (userId: string) => {
    if (await window.appConfirm('Bu kullanıcının şifresini sıfırlamak istediğinize emin misiniz?')) {
      console.log('Password reset for user:', userId);
      alert('Şifre sıfırlama linki kullanıcının email adresine gönderildi.');
    }
  };

  const handleUpdatePermissions = (userId: string, newPermissions: string[]) => {
    setUsers(prev => prev.map(user =>
      user.id === userId ? { ...user, permissions: newPermissions } : user
    ));
  };

  const handleDeleteUser = async (targetUser: UserType) => {
    if (targetUser.id === currentUser?.id) {
      alert('Kendi hesabınızı silemezsiniz.');
      return;
    }

    const confirmed = await window.appConfirm(
      `${targetUser.name} hesabını silmek istediğinize emin misiniz?\n\n` +
      'Kullanıcı artık giriş yapamaz. Teklifler ve geçmiş kayıtlar silinmez; kullanıcı UUID bağlantısı arşivde korunur.'
    );
    if (!confirmed) return;

    try {
      setDeletingUserId(targetUser.id);
      if (!supabase) throw new Error('Supabase bağlantısı bulunamadı.');

      const { data, error } = await supabase.functions.invoke('delete-user', {
        body: { userId: targetUser.id },
      });

      if (error) throw error;
      if (!data?.success) throw new Error(data?.error || 'Kullanıcı silinemedi.');

      await fetchUsers();
      alert('Hesap silindi. Teklifler ve geçmiş kayıt bağlantıları korundu.');
    } catch (error: any) {
      console.error('Kullanıcı silme hatası:', error);
      alert(error?.message || 'Kullanıcı silinirken bir hata oluştu.');
    } finally {
      setDeletingUserId(null);
    }
  };

  const getUnitTypeName = (type: string): string => {
    switch (type) {
      case 'education_consultant': return 'Eğitim Danışmanı';
      case 'visa_consultant': return 'Vize Danışmanı';
      case 'marketing': return 'Pazarlama';
      case 'accounting': return 'Muhasebe';
      case 'customer_service': return 'Müşteri Hizmetleri';
      case 'sales': return 'Satış';
      case 'testprep': return 'Test Hazırlık';
      case 'teknik_destek': return 'Teknik Destek';
      default: return type;
    }
  };

  const getRoleText = (role: UserType['role']) => {
    switch (role) {
      case 'admin': return 'Sistem Yöneticisi';
      case 'branch': return 'Şube Yöneticisi';
      case 'unit': return 'Birim Çalışanı';
      case 'student': return 'Öğrenci';
      default: return role;
    }
  };

  const getRoleColor = (role: UserType['role']) => {
    switch (role) {
      case 'admin': return 'bg-red-100 text-red-800 dark:bg-red-900/20 dark:text-red-400';
      case 'branch': return 'bg-blue-100 text-blue-800 dark:bg-blue-900/20 dark:text-blue-400';
      case 'unit': return 'bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-400';
      case 'student': return 'bg-purple-100 text-purple-800 dark:bg-purple-900/20 dark:text-purple-400';
      default: return 'bg-gray-100 text-gray-800 dark:bg-gray-900/20 dark:text-gray-400';
    }
  };

  const getBranchName = (branchId?: string) => {
    if (!branchId) return '-';
    const branch = branches.find(b => b.id === branchId);
    return branch ? branch.name : 'Bilinmeyen Şube';
  };

  const getUnitName = (unitId?: string) => {
    if (!unitId) return '-';
    const unit = units.find(u => u.id === unitId);
    return unit ? unit.name : 'Bilinmeyen Birim';
  };

  const filteredUsers = users.filter(user => {
    const matchesSearch =
      user.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.email.toLowerCase().includes(searchTerm.toLowerCase());

    const matchesRole = roleFilter === 'all' || user.role === roleFilter;
    const matchesStatus = statusFilter === 'all' ||
      (statusFilter === 'active' && user.isActive) ||
      (statusFilter === 'inactive' && !user.isActive);

    return matchesSearch && matchesRole && matchesStatus;
  });


  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-4 sm:space-y-0">
        <div>
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white">Kullanıcılar</h2>
          <p className="text-gray-600 dark:text-gray-400">Sistem kullanıcılarını yönetin</p>
        </div>
        <button
          onClick={() => setShowAddModal(true)}
          className="flex items-center space-x-2 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg transition-colors"
        >
          <Plus className="w-4 h-4" />
          <span>Yeni Kullanıcı</span>
        </button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-xl p-6">
          <div className="flex items-center space-x-3">
            <User className="w-8 h-8 text-blue-500" />
            <div>
              <p className="text-2xl font-bold text-gray-900 dark:text-white">{users.length}</p>
              <p className="text-sm text-gray-600 dark:text-gray-400">Toplam Kullanıcı</p>
            </div>
          </div>
        </div>
        <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-xl p-6">
          <div className="flex items-center space-x-3">
            <CheckCircle className="w-8 h-8 text-green-500" />
            <div>
              <p className="text-2xl font-bold text-gray-900 dark:text-white">
                {users.filter(u => u.isActive).length}
              </p>
              <p className="text-sm text-gray-600 dark:text-gray-400">Aktif Kullanıcı</p>
            </div>
          </div>
        </div>
        <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-xl p-6">
          <div className="flex items-center space-x-3">
            <Shield className="w-8 h-8 text-purple-500" />
            <div>
              <p className="text-2xl font-bold text-gray-900 dark:text-white">
                {users.filter(u => u.role === 'admin').length}
              </p>
              <p className="text-sm text-gray-600 dark:text-gray-400">Yönetici</p>
            </div>
          </div>
        </div>
        <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-xl p-6">
          <div className="flex items-center space-x-3">
            <Calendar className="w-8 h-8 text-yellow-500" />
            <div>
              <p className="text-2xl font-bold text-gray-900 dark:text-white">
                {users.filter(u => {
                  const now = new Date();
                  return u.createdAt.getMonth() === now.getMonth() &&
                    u.createdAt.getFullYear() === now.getFullYear();
                }).length}
              </p>
              <p className="text-sm text-gray-600 dark:text-gray-400">Bu Ay Yeni</p>
            </div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-xl p-6">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
            <input
              type="text"
              placeholder="Kullanıcı ara..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2.5 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>

          <select
            value={roleFilter}
            onChange={(e) => setRoleFilter(e.target.value)}
            className="bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg px-4 py-2.5 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          >
            <option value="all">Tüm Roller</option>
            <option value="admin">Sistem Yöneticisi</option>
            <option value="branch">Şube Yöneticisi</option>
            <option value="unit">Birim Çalışanı</option>
            <option value="student">Öğrenci</option>
          </select>

          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg px-4 py-2.5 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          >
            <option value="all">Tüm Durumlar</option>
            <option value="active">Aktif</option>
            <option value="inactive">Pasif</option>
          </select>

          <button className="flex items-center justify-center space-x-2 bg-gray-100 hover:bg-gray-200 dark:bg-gray-800 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300 px-4 py-2.5 rounded-lg transition-colors">
            <Filter className="w-4 h-4" />
            <span>Filtrele</span>
          </button>
        </div>
      </div>

      {/* Users Table */}
      <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-xl overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700">
                <tr>
                  <th className="text-left py-3 px-6 text-sm font-medium text-gray-900 dark:text-white">Kullanıcı</th>
                  <th className="text-left py-3 px-6 text-sm font-medium text-gray-900 dark:text-white">İletişim</th>
                  <th className="text-left py-3 px-6 text-sm font-medium text-gray-900 dark:text-white">Rol</th>
                  <th className="text-left py-3 px-6 text-sm font-medium text-gray-900 dark:text-white">Durum</th>
                  <th className="text-left py-3 px-6 text-sm font-medium text-gray-900 dark:text-white">Son Giriş</th>
                  <th className="text-left py-3 px-6 text-sm font-medium text-gray-900 dark:text-white">Kayıt Tarihi</th>
                  <th className="text-right py-3 px-6 text-sm font-medium text-gray-900 dark:text-white">İşlemler</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                {filteredUsers.map((user) => (
                  <tr key={user.id} className="hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors">
                    <td className="py-4 px-6">
                      <div className="flex items-center space-x-3">
                        <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-purple-500 rounded-full flex items-center justify-center">
                          <span className="text-white text-sm font-semibold">
                            {user.name.split(' ').map(n => n[0]).join('')}
                          </span>
                        </div>
                        <div>
                          <p className="font-medium text-gray-900 dark:text-white">{user.name}</p>
                          <p className="text-sm text-gray-500 dark:text-gray-400">ID: {user.id}</p>
                        </div>
                      </div>
                    </td>
                    <td className="py-4 px-6">
                      <div className="space-y-1">
                        <div className="flex items-center space-x-2 text-sm text-gray-600 dark:text-gray-400">
                          <Mail className="w-4 h-4" />
                          <span className="truncate max-w-32">{user.email}</span>
                        </div>
                        {user.phone && (
                          <div className="flex items-center space-x-2 text-sm text-gray-600 dark:text-gray-400">
                            <Phone className="w-4 h-4" />
                            <span>{user.phone}</span>
                          </div>
                        )}
                      </div>
                    </td>
                    <td className="py-4 px-6">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getRoleColor(user.role)}`}>
                        {getRoleText(user.role)}
                      </span>
                    </td>
                    <td className="py-4 px-6">
                      <div className="flex items-center space-x-2">
                        {user.isActive ? (
                          <CheckCircle className="w-5 h-5 text-green-500" />
                        ) : (
                          <XCircle className="w-5 h-5 text-red-500" />
                        )}
                        <span className={`text-sm ${user.isActive ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}>
                          {user.isActive ? 'Aktif' : 'Pasif'}
                        </span>
                      </div>
                    </td>
                    <td className="py-4 px-6">
                      <span className="text-gray-600 dark:text-gray-400 text-sm">
                        {user.lastLogin ? format(user.lastLogin, 'dd MMM yyyy', { locale: tr }) : 'Hiç giriş yapmadı'}
                      </span>
                    </td>
                    <td className="py-4 px-6">
                      <span className="text-gray-600 dark:text-gray-400 text-sm">
                        {format(user.createdAt, 'dd MMM yyyy', { locale: tr })}
                      </span>
                    </td>
                    <td className="py-4 px-6">
                      <div className="flex items-center justify-end space-x-2">
                        <button
                          onClick={() => handleUserAction('view', user.id)}
                          className="p-2 text-gray-500 hover:text-blue-600 dark:text-gray-400 dark:hover:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded-lg transition-colors"
                          title="Kullanıcı Detayları"
                        >
                          <Eye className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleUserAction('edit', user.id)}
                          className="p-2 text-gray-500 hover:text-green-600 dark:text-gray-400 dark:hover:text-green-400 hover:bg-green-50 dark:hover:bg-green-900/20 rounded-lg transition-colors"
                          title="Kullanıcı Düzenle"
                        >
                          <Edit className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleUserAction('security', user.id)}
                          className="p-2 text-gray-500 hover:text-purple-600 dark:text-gray-400 dark:hover:text-purple-400 hover:bg-purple-50 dark:hover:bg-purple-900/20 rounded-lg transition-colors"
                          title="Güvenlik Ayarları"
                        >
                          <Shield className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleDeleteUser(user)}
                          disabled={deletingUserId === user.id || user.id === currentUser?.id}
                          className="p-2 text-gray-500 hover:text-red-600 dark:text-gray-400 dark:hover:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors disabled:cursor-not-allowed disabled:opacity-40"
                          title={user.id === currentUser?.id ? 'Kendi hesabınızı silemezsiniz' : 'Hesabı Sil'}
                        >
                          <Trash2 className={`w-4 h-4 ${deletingUserId === user.id ? 'animate-pulse' : ''}`} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Add User Modal */}
      <AddUserModal
        isOpen={showAddModal}
        onClose={() => setShowAddModal(false)}
        onSave={handleSaveUser}
      />

      {/* User Detail Modal */}
      <UserDetailModal
        isOpen={showDetailModal}
        user={selectedUser}
        branches={branches}
        units={units}
        onClose={() => setShowDetailModal(false)}
        onOpenEdit={() => setShowEditModal(true)}
        onOpenSecurity={() => setShowSecurityModal(true)}
      />

      {/* Edit User & Multi-Department Assignment Modal */}
      <EditUserModal
        isOpen={showEditModal}
        user={selectedUser}
        onClose={() => setShowEditModal(false)}
        onSave={fetchUsers}
      />

      {/* Security Modal */}
      <UserSecurityModal
        isOpen={showSecurityModal}
        user={selectedUser}
        onClose={() => setShowSecurityModal(false)}
        onUpdate={fetchUsers}
      />
    </div>
  );
};

export default Users;
